import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnDestroy } from '@angular/core';
import { ArchiveMemberInfo, Extension, ExtensionScope, initExtension } from '@flywheel-io/extension';
import { FwButtonModule } from '@flywheel-io/vision';
import { firstValueFrom, from, mergeMap, Observable, startWith, Subject, Subscription, switchMap, takeUntil, throwError } from 'rxjs';

import { D3GeoTile, Tile, TileArray } from './geo-tile';
import { initMock } from '../mock-extension';
// D3 will be loaded globally via script tag to avoid UMD context issues
declare const d3: any;

interface ViewerDialogData {
  title?: string
  url?: string
}

interface MontageTileConfig {
  dirname: string
  real_size: [number, number]
  zoom_levels: Record<string, [number, number]>
  mimetype: string
  tile_size: number
}

@Component({
  selector: 'app-root',
  imports: [
    FwButtonModule,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnDestroy {
  extension: Extension | null = null;
  tileInfo: MontageTileConfig = {
    dirname: '',
    real_size: [0, 0],
    zoom_levels: {},
    mimetype: 'image/jpeg',
    tile_size: 256,
  };
  rectangle = { maxX: 0, maxY: 0, minX: 0, minY: 0 };
  actualSize = { width: 0, height: 0 };
  width: number = 0;
  height: number = 0;
  maxScale: number = 0;
  minScale: number = 0;
  initScale: number = 0;
  zoom: any;
  tile: any;
  layer: any;
  map: any;
  prefix: string = '';
  cacheData: Record<string, string> = {};

  dataSubscriptions = new Map<string, Subscription>();
  requestQueue = new Subject<{ tile: any, image: any }>();
  zoomChange = new Subject<void>();

  subscriptions = {
    zoom: Subscription.EMPTY,
  }
  
  private runningLocally = false;

  constructor(private http: HttpClient){
    this.runningLocally = window.top === window.self;
    
    // Wait for D3 to be available, then load d3-geo-tile script
    this.loadD3GeoTile().then(() => {
      const init = this.runningLocally ? initMock : initExtension;
      init({ scope: ExtensionScope.ReadOnly, validateOrigin: () => true })
        .then((extension) => {
          this.extension = extension;
          return this.extension?.getArchiveMembers(this.extension.file);
        })
        .then((response) => {
          this.initViewer(response);
        });
    });
  }

  ngOnDestroy(): void {
    this.dataSubscriptions.forEach(subscription => subscription.unsubscribe());
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  initViewer(data?: ArchiveMemberInfo): void {
    if (!data || !data.comment) {
      console.error('No valid data provided to initialize the viewer.');
      return;
    }
    this.subscriptions.zoom = this.zoomChange.pipe(
      startWith(undefined),
      switchMap(() => this.requestQueue.pipe(
        mergeMap(({ tile, image }) => this.processTileRequest(tile, image).pipe(
          takeUntil(this.zoomChange),
        ), 5)) // limit to 5 concurrent requests
      ),
    ).subscribe();

    this.tileInfo = JSON.parse(data.comment);
    let maxZ: number = 0, minZ: number = 0;
    for (const z of Object.keys(this.tileInfo!.zoom_levels)) {
      const numZ = Number(z);
      if (typeof maxZ === 'undefined') {
        maxZ = numZ;
        minZ = numZ;
      } else {
        if (maxZ < numZ) {
          maxZ = numZ;
        } else if (minZ > numZ) {
          minZ = numZ;
        }
      }
    }

    const width = d3.select('div#montage-viewer').style('width');
    const height = d3.select('div#montage-viewer').style('height');
    this.maxScale = 1 << (maxZ + 8);
    this.prefix = this.prefixMatch(['webkit', 'ms', 'Moz', 'O']);
    this.minScale = 1 << (minZ + 8);

    this.maxScale *= 1.414; //rough approximation of sqrt(2)

    this.width = Number(width.substr(0, width.length - 2));
    this.height = Number(height.substr(0, height.length - 2));

    let initSize, initTiles, initZ = maxZ + 1;
    this.actualSize = {
      width: this.tileInfo!.real_size[0],
      height: this.tileInfo!.real_size[1],
    };

    do {
      initZ--;
      initTiles = this.tileInfo!.zoom_levels[initZ];
      initSize = {
        width: initTiles[0] * 256,
        height: initTiles[1] * 256,
      };
      while (this.actualSize.width > initSize.width || this.actualSize.height > initSize.height) {
        this.actualSize.width /= 2;
        this.actualSize.height /= 2;
      }
    } while ((this.actualSize.width > this.width || this.actualSize.height > this.height || initZ === minZ) && initZ > 0);

    this.initScale = 1 << (initZ + 8);

    this.tile = (d3 as any).geo.tile()
      .size([this.width, this.height]);

    this.zoom = d3.behavior.zoom()
      .center([this.width / 2, this.height / 2])
      .scale(this.initScale)
      .scaleExtent([this.minScale, this.maxScale])
      .translate([
        this.initScale / 2 - this.actualSize.width / 2 + this.width / 2,
        this.initScale / 2 - this.actualSize.height / 2 + this.height / 2,
      ])
      .on('zoom', () => this.onZoomHandler());

    this.map = d3.select('div.montage-d3-map')
      .style('width', this.width + 'px')
      .style('height', this.height + 'px')
      .call(this.zoom.bind(this));

    this.layer = this.map.append('div')
      .attr('class', 'montage-d3-layer');

    this.onZoomHandler();
  }

  getMemberPath(z: string, x: string, y: string): string {
    const member = [
      this.tileInfo!.dirname,
      'z' + this.padStart(z, 3, '0'),
      (
        'x' + this.padStart(x, 3, '0') + '_' +
        'y' + this.padStart(y, 3, '0') + '.jpg'
      ),
    ].join('/');
    return member;
  }

  getData(z: string, x: string, y: string): Observable<Blob> {
    const member = this.getMemberPath(z, x, y);

    if (!this.extension) {
      return throwError('No extension available');
    }
    return from(this.extension.getFileBlob(this.extension.file, {member}));
  }

  calculateRectangle(tiles: any): { maxX: number, maxY: number, minX: number, minY: number } {
    if (tiles.translate) {
      const minX = Math.floor(- tiles.translate[0]);
      const minY = Math.floor(- tiles.translate[1]);
      this.rectangle = {
        maxX: minX + 1.5 * this.width / 256 + 1,
        maxY: minY + 1.5 * this.height / 256 + 1,
        minX: minX,
        minY: minY,
      };
    }
    return this.rectangle;
  }
  inRectangle(x: any, y: any, z: any): boolean {
    const level = this.tileInfo!.zoom_levels[z];
    return (
      x <= this.rectangle.maxX && x >= this.rectangle.minX &&
      y <= this.rectangle.maxY && y >= this.rectangle.minY &&
      x < level[0] && y < level[1]
    );
  }

  onZoomHandler(): void {
    if (!this.tile || !this.layer || !this.map) { // should all be initialized by now
      return;
    }
    const scaled = this.tile
      .scale(this.zoom.scale()) as D3GeoTile;
    const tiles = scaled.translate(this.zoom.translate())() as TileArray;
    this.calculateRectangle(tiles);

    const tileElements = this.layer
      .style(this.prefix + 'transform', this.matrix3d(tiles.scale, tiles.translate))
      .selectAll('.montage-d3-tile');
    const imageStruct = tileElements.data(
      tiles.filter(d => this.inRectangle(d[0], d[1], d[2])),
      (d: unknown) => d,
    );
    imageStruct.exit().remove();

    imageStruct.enter()
      .append('img')
      .attr('class', 'montage-d3-tile')
      .style('left', function (d: any) { return (d[0] << 8) + 'px'; })
      .style('top', function (d: any) { return (d[1] << 8) + 'px'; })
      .each(
        (tile: any, imageIndex: any) => {
          this.requestQueue.next({ tile, image: imageStruct[0][imageIndex] });
        }
      );
  }

  private processTileRequest(tile: number[], image: Element): Observable<void> {
    return new Observable<void>((observer: any) => {
      const cacheKey = this.getCacheKey(tile);
      if (this.cacheData[cacheKey]) {
        image.setAttribute('src', this.cacheData[cacheKey]);
        observer.complete();
        return;
      }
      const existingSubscription = this.dataSubscriptions.get(cacheKey);
      if (existingSubscription) {
        existingSubscription.unsubscribe();
      }
      this.dataSubscriptions.set(cacheKey, this.getData(tile[2].toString(), tile[0].toString(), tile[1].toString()).subscribe({
        next: (response: Blob) => {
          const reader = new FileReader();
          reader.onload = () => {
            this.cacheData[cacheKey] = reader.result as string;
            image.setAttribute('src', this.cacheData[cacheKey]);
            observer.complete();
          };
          reader.onerror = (error) => {
            observer.error(error);
          };
          reader.readAsDataURL(response);
        },
        error: (error: any) => {
          observer.error(error);
        },
      }));
    });
  }

  zoomButtonClicked(incrementor: number): void {
    if (!this.tile || !this.layer || !this.map) { // should all be initialized by now
      return;
    }
    // cancel pending requests
    this.dataSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.zoomChange.next();

    let newScale = this.zoom.scale() * Math.pow(2, incrementor);
    if (newScale > this.maxScale) {
      newScale = this.maxScale;
    }
    if (newScale < this.minScale) {
      newScale = this.minScale;
    }
    this.map.call(this.zoom.event); // https://github.com/mbostock/d3/issues/2387

    // Record the coordinates (in data space) of the center (in screen space).
    const center0 = this.zoom.center(), translate0 = this.zoom.translate(), coordinates0 = this.coordinates(center0);
    this.zoom.scale(newScale);

    // Translate back to the center.
    const center1 = this.point(coordinates0);
    this.zoom.translate([translate0[0] + center0[0] - center1[0], translate0[1] + center0[1] - center1[1]]);

    this.map.transition().duration(750).call(this.zoom.event);
  }

  coordinates(point: any): [number, number] {
    const scale = this.zoom.scale(), translate = this.zoom.translate();
    return [(point[0] - translate[0]) / scale, (point[1] - translate[1]) / scale];
  }

  point(coordinates: any): [number, number] {
    const scale = this.zoom.scale(), translate = this.zoom.translate();
    return [coordinates[0] * scale + translate[0], coordinates[1] * scale + translate[1]];
  }

  matrix3d(scale: any, translate: any): string {
    const k = scale / 256, r = scale % 1 ? Number : Math.round;
    return 'matrix3d(' + [k, 0, 0, 0, 0, k, 0, 0, 0, 0, 1, 0, r(translate[0] * scale), r(translate[1] * scale), 0, 1] + ')';
  }

  prefixMatch(p: any): string {
    let i = -1;
    const n = p.length, s = document.body.style;
    while (++i < n) { if (p[i] + 'Transform' in s) { return '-' + p[i].toLowerCase() + '-'; } }
    return '';
  }

  getCacheKey(tile: number[]): string {
    return [tile[2], tile[0], tile[1]].join(':');
  }

  reset(): void {
    if (!this.tile || !this.layer || !this.map) { // should all be initialized by now
      return;
    }
    this.zoom.scale(this.initScale).translate([
      this.initScale / 2 - this.actualSize.width / 2 + this.width / 2,
      this.initScale / 2 - this.actualSize.height / 2 + this.height / 2,
    ]);
    this.map.call(this.zoom.event);
  }

  private padStart(str: string, length: number, padString: string): string {
    return str.padStart(length, padString);
  }

  private async loadD3GeoTile(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!(window as any).d3.geo) {
        (window as any).d3.geo = {};
      }
      
      // Load the d3-geo-tile script
      const script = document.createElement('script');
      script.src = 'd3-geo-tile.js';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load d3-geo-tile.js'));
      document.head.appendChild(script);
    });
  }

}
